import { alert } from '@/services/alert';
import { TEXT_CONTENT_PAYLOAD_ID } from '@bariendo/lib/constants';
import { PatientAccessCallAnswers } from '@bariendo/lib/patient-access-note';
import { Communication } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useCallback, useEffect, useRef, useState } from 'react';

/** Long enough to coalesce a burst of chip taps, short enough that a dropped call loses nothing. */
const AUTOSAVE_DEBOUNCE_MS = 1500;

interface UsePatientAccessCallDraftOptions<T extends PatientAccessCallAnswers> {
  note: Communication;
  initialAnswers: T;
  /** Payload entry the structured answers live on — one id per script. */
  answersPayloadId: string;
  /** Human-readable summary for the text payload, so search shows something. */
  buildSummary: (answers: T) => string;
  onSaved?: () => void;
}

/**
 * Holds the rep's answers locally and writes them back to the note.
 *
 * A dropped call must not lose notes, so edits autosave on a debounce while the
 * rep works. "Save to chart" flushes whatever is pending and moves the
 * Communication to `completed`, which is what collapses the card.
 *
 * Autosave failures are surfaced but never block typing — the local state stays
 * authoritative and the next edit retries.
 */
export function usePatientAccessCallDraft<T extends PatientAccessCallAnswers>({
  note,
  initialAnswers,
  answersPayloadId,
  buildSummary,
  onSaved,
}: UsePatientAccessCallDraftOptions<T>) {
  const medplum = useMedplum();
  const [answers, setAnswers] = useState<T>(initialAnswers);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);

  /** Latest answers, so the debounce timer and unmount flush never write a stale snapshot. */
  const answersRef = useRef(answers);
  answersRef.current = answers;

  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  /** Set once the rep has actually changed something; a freshly opened card must not write. */
  const dirtyRef = useRef(false);

  /**
   * Writes the answers onto the *stored* note, not onto the one this hook was given.
   *
   * The timeline reads Communications through `PATIENT_TIMELINE_QUERY`, so the note
   * handed down is a GraphQL **view** of the resource rather than the resource:
   * it hydrates `subject.resource`, `sender.resource` and `basedOn[].resource`,
   * returns every requested-but-absent scalar as `null`, drops
   * `meta.security.system`, and carries a synthetic `assigneeTask` the mapper adds.
   *
   * Spreading that into `updateResource` is rejected outright — captured from the
   * browser against staging:
   *
   *     400  Invalid additional property "resource"      Communication.sender.resource
   *     400  Invalid additional property "resource"      Communication.subject.resource
   *     400  Invalid additional property "assigneeTask"  Communication.assigneeTask
   *
   * so every autosave and every Save to chart on a real card failed with
   * "Couldn't autosave". An earlier fix stripped the nulls, which was the same
   * problem seen through a narrower hole; nothing can be written back safely while
   * the payload is the view. Re-reading is also what makes the stored status
   * authoritative, so a note charted in another tab is not reverted to a draft.
   */
  const persist = useCallback(
    async (next: T, status?: Communication['status']) => {
      if (!note.id) {
        throw new Error('A patient-access note must exist before its answers can be saved');
      }

      const stored = await medplum.readResource('Communication', note.id);

      const payload = [
        ...(stored.payload ?? []).filter(
          (entry) => entry.id !== TEXT_CONTENT_PAYLOAD_ID && entry.id !== answersPayloadId
        ),
        { id: TEXT_CONTENT_PAYLOAD_ID, contentString: buildSummary(next) },
        { id: answersPayloadId, contentString: JSON.stringify(next) },
      ];

      // Autosave passes no status and must preserve whatever the note already is.
      await medplum.updateResource<Communication>({ ...stored, status: status ?? stored.status, payload });
    },
    // `note.id` rather than `note`: the object identity changes on every
    // live-timeline poll, and nothing else about it is read here any more.
    [medplum, note.id, answersPayloadId, buildSummary]
  );

  const flush = useCallback(async () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = undefined;
    }
    if (!dirtyRef.current) return;

    try {
      await persist(answersRef.current);
      dirtyRef.current = false;
      setSaveError(false);
    } catch {
      setSaveError(true);
    }
  }, [persist]);

  const updateAnswers = useCallback(
    (mutate: (current: T) => T) => {
      const current = answersRef.current;
      const next = mutate(current);

      // A mutation that changes nothing must not mark the note dirty. Callers
      // include effects that re-run whenever the note object identity changes
      // (every live-timeline poll), so without this a no-op re-application —
      // eligibility pre-fill re-writing values that are already set — would
      // queue a redundant write on every tick.
      if (JSON.stringify(next) === JSON.stringify(current)) return;

      // Side effects stay out of the setState updater: updaters must be pure,
      // and React may invoke them more than once.
      answersRef.current = next;
      dirtyRef.current = true;
      setAnswers(next);

      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        void flush();
      }, AUTOSAVE_DEBOUNCE_MS);
    },
    [flush]
  );

  const setValue = useCallback(
    (id: string, value: string | string[] | boolean | undefined) => {
      updateAnswers((current) => ({ ...current, values: { ...current.values, [id]: value } }));
    },
    [updateAnswers]
  );

  /** Save to chart. Finalises the note; the card collapses to its outcome line. */
  const saveToChart = useCallback(async () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = undefined;
    }

    setIsSaving(true);
    try {
      await persist(answersRef.current, 'completed');
      dirtyRef.current = false;
      setSaveError(false);
      onSaved?.();
    } catch (error) {
      setSaveError(true);
      alert.error(`Couldn't save the call to the chart: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsSaving(false);
    }
  }, [persist, onSaved]);

  /**
   * Kept in a ref so the unmount effect below can stay on an empty dependency
   * list. Keying that effect on `flush` would re-run its cleanup every time the
   * note object identity changes — which is every live-timeline poll — turning
   * each pending debounce into an immediate write.
   */
  const flushRef = useRef(flush);
  flushRef.current = flush;

  // A rep who navigates away mid-call still keeps the answers already entered.
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        void flushRef.current();
      }
    };
  }, []);

  /**
   * Writes pending answers now rather than on the debounce. For a record that
   * must not be lost to a closed tab, e.g. that a coverage button already
   * created its task.
   */
  const saveNow = useCallback(() => flush(), [flush]);

  return { answers, updateAnswers, setValue, saveToChart, saveNow, isSaving, saveError };
}
